import { useRef, useEffect, useState } from 'react';
import Layout from '../../../Layout/Layout';
import { Container, Row, Col, Button, Spinner, Modal, ModalBody, ModalFooter } from 'reactstrap';
import {
  useToast,
  useTranslator,
  useAppContext,
  cloudflareImage,
  mealPlaceholderImage,
  withLoading,
} from '@front-activerse/common-components';
import { withMobileAppMode } from '@front-activerse/common-components';
import DayPlanMealNavigation from './DayPlanMealNavigation';
import { useOutletContext, useNavigate, Link } from 'react-router-dom';
import TitleBar from '../../../Layout/TitleBar';
import UniversalPlaceholder from '../../../Layout/UniversalPlaceholder';

import { formatIsoDate } from '@front-activerse/common-components';
import DayPlanNutritions from './DayPlanNutritions';
import DayPlanMealRating from './DayPlanMealRating';
import InstacartButton from './InstacartButton';
import FriscoButton from './FriscoButton';

const DayPlanMealDetails = ({
  children,
  loadDayPlanMealReplacements,
  addMealToFavorites,
  removeMealFromFavorites,
  date,
  originalMealId = undefined,
  setId,
  mealSiblings,
  meal,
  rateMeal,
  refetch,
}) => {
  const videoRef = useRef();
  const ratingRef = useRef();

  const { t } = useTranslator();
  const { toastSuccess, toastError, dismiss } = useToast();
  const [showModal, setShowModal] = useState(false);
  const { trackFirebaseEvent } = useOutletContext();
  const navigate = useNavigate();

  const [preloadInside, setPreloadInside] = useState(false);
  const [visiblePoster, setVisiblePoster] = useState(true);

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const {
    consts: { APP_BRAND },
    images: {
      favouriteFull,
      favouriteEmpty,
      playBtn,
      ratingButtonEmpty,
      ratingButtonFull,
      buttonChange,
      infoIcon,
      addToTrackerIcon,
    },
  } = useAppContext();

  useEffect(() => {
    trackFirebaseEvent('screen_view', {
      firebase_screen: 'Meal_details',
      meal_id: meal.id,
      meal_category: meal.mealType.name,
    });
  }, []);

  const calculatePreparationTime = (minutes) => {
    const steps = [5, 15, 30, 45, 60];

    if (minutes === 0 || minutes == null) {
      return null;
    }

    for (const step of steps) {
      if (minutes <= step) {
        return `< ${step} min`;
      }
    }
    return `> ${steps[steps.length - 1]} min`;
  };

  const handleMealReplace = () => {
    loadMealReplacements();
    localStorage.setItem('activeTimestamp', 2);
    trackFirebaseEvent('user_action', {
      action: 'tap-change_meal',
      location: 'Meal_details',
      meal_id: meal.id,
      meal_category: meal.mealType.name,
    });
  };

  const toggleFavorite = async () => {
    try {
      if (meal.isFavorite) {
        const response = await removeMealFromFavorites.removeMeal(meal.id);
        if (
          response.data.me.removeMealFromFavoritesByMealId.__typename === 'BasicMutationSuccess'
        ) {
          toastSuccess(
            <p className="mb-0">
              {t('mealPlan.favorites.notification.removedFromFav.content')}
              <Button
                color="link"
                onClick={() => {
                  addMealToFavorites.addMeal(meal.id);
                  dismiss();
                }}
              >
                {t('meal.favorites.notification.removedFromFav.button')}
              </Button>
            </p>
          );
        }
      } else {
        const response = await addMealToFavorites.addMeal(meal.id);
        if (response.data.me.addMealToFavorites.__typename === 'BasicMutationSuccessWithId') {
          toastSuccess(
            <p className="mb-0">{t('mealPlan.favorites.notification.addedToFav.content')}</p>
          );
        }
      }

      trackFirebaseEvent('user_action', {
        action: 'tap-add_favourites',
        location: 'Meal_details',
        meal_id: meal.id,
        meal_category: meal.mealType.name,
      });
    } catch (err) {
      toastError();
    }
  };

  const loadMealReplacements = async () => {
    setPreloadInside(true);
    const request = {
      userDietSetId: setId,
      mealType: meal.mealType.type,
      dietMealId: meal.id,
      limit: 10,
      prependWithOriginalMeal: !!originalMealId,
    };
    const result = await loadDayPlanMealReplacements.load(request);

    if (result.data.me.dailyDietMeal) {
      const replacements = result.data.me.dailyDietMeal.replacements;

      const url = `/meals/day-plan/${formatIsoDate(date)}/${meal.id}/${
        meal.mealType.type
      }/${setId}/${originalMealId}/replace`;

      navigate(url, {
        state: {
          replacements,
          from: 'meal-details',
        },
      });
    }
    setPreloadInside(false);
  };

  const renderMealVideo = () => {
    if (!meal.preparationVideoUrl) {
      return renderMealImage();
    }

    return (
      <div className="embed-responsive ratio ratio-1x1">
        <video
          ref={videoRef}
          src={meal.preparationVideoUrl}
          controls
          playsInline
          controlsList="nodownload"
          disablePictureInPicture
          onEnded={() => setVisiblePoster(true)}
        />
        {visiblePoster ? (
          <div
            style={{
              backgroundImage: `url(${meal.preparationImageUrl
                .replace('(', '%28')
                .replace(')', '%29')})`,
            }}
            className="embed-responsive-item d-flex align-items-center justify-content-center"
            onClick={() => playVideo(videoRef)}
          >
            <img src={playBtn} alt="" />
          </div>
        ) : (
          ''
        )}
      </div>
    );
  };

  const renderMealImage = () => {
    let mealImg;
    if (meal.preparationImageUrl) {
      mealImg = meal.preparationImageUrl;
    } else {
      mealImg = mealPlaceholderImage(meal.mealType.type, APP_BRAND);
    }

    const coverImage = cloudflareImage(mealImg, `${APP_BRAND}`, 'mealPlanMealDetails');

    return <img src={coverImage} className="img-fluid" width={768} height={768} alt="" />;
  };

  const playVideo = async (myVideoRef) => {
    setVisiblePoster(false);

    trackFirebaseEvent('user_action', {
      action: 'tap-watch_video',
      location: 'Meal_details',
      meal_id: meal.id,
      meal_category: meal.mealType.name,
    });

    try {
      await myVideoRef.current.play();
    } catch (err) {
      toastError();
      console.warn(err);
    }
  };

  const renderTags = () => {
    if (meal.mealCustomTag.label) {
      return (
        <div className="tags">
          {meal.mealCustomTag && (
            <span
              className="tag me-1"
              style={{ backgroundColor: meal.mealCustomTag.color, color: '#fff' }}
            >
              {meal.mealCustomTag.label}
            </span>
          )}
        </div>
      );
    }
    return null;
  };

  const handleAddMealToTracker = () => {
    const isTrackerEnabled = localStorage.getItem('trackerEnabled') === 'true';

    if (isTrackerEnabled) {
      navigate(`/meals/day-plan/module/tracker/add-meal/${meal.id}/${meal.mealType.type}/${setId}`);
    } else {
      toggleModal();
    }
  };

  const calculatedPreparationTime = calculatePreparationTime(meal.preparationTime.minutes);

  return (
    <Layout>
      <TitleBar level={1}>{t(`mealPlan.mealDetails.navbarTitle`)}</TitleBar>
      <div className="page-template-meal-plan">
        <Container className="py-md-4">
          <Row>
            <Col xl={{ size: 10, offset: 1 }}>
              <div className="page-container">
                <DayPlanMealNavigation
                  date={date}
                  events={mealSiblings.dailyDietPlan.events}
                  activeMeal={meal.id}
                  setId={setId}
                />
                <div className="meal-details">
                  <Row>
                    <Col md="6">
                      <div className="meal-image-video">
                        {renderMealVideo()}
                        {meal.canBeAddedToFavorites && (
                          <Button className="add-remove-fav" color="link" onClick={toggleFavorite}>
                            <img
                              src={meal.isFavorite ? favouriteFull : favouriteEmpty}
                              alt=""
                              width="32"
                              height="32"
                            />
                          </Button>
                        )}

                        {visiblePoster && (
                          <div className="meal-info2 d-md-none">
                            <Container>
                              {renderTags()}
                              <h1 className="meal-title title-large semi-bold white mb-0">
                                {meal.name}
                              </h1>
                            </Container>
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="instacart-container d-md-none">
                        <InstacartButton
                          meal={meal}
                          publicName={meal.name}
                          preparationImageUrl={meal.preparationImageUrl}
                        />
                        <FriscoButton meal={meal} publicName={meal.name} />
                      </div>

                      <div className="meal-info">
                        <div className="d-none d-md-block">
                          {renderTags()}
                          <h1 className="meal-title title-large semi-bold mb-xl">{meal.name}</h1>
                        </div>

                        <DayPlanMealRating
                          rateMeal={rateMeal}
                          refetch={refetch}
                          meal={meal}
                          reference={ratingRef}
                        />

                        {calculatedPreparationTime && (
                          <div className="grey-box mb-2">
                            {t('mealDetails.info.prepTime.title')}{' '}
                            <strong className="float-end">{calculatedPreparationTime}</strong>
                          </div>
                        )}

                        <div className="d-none d-md-block">
                          <DayPlanNutritions kcal={meal.kcal} macro={meal.macro} />
                        </div>

                        <div className="action-buttons d-none d-md-block">
                          <Row className="gx-0">
                            <Col xs={3}>
                              <Button
                                color="link"
                                size="sm"
                                onClick={() => handleMealReplace()}
                                className="w-100"
                              >
                                {preloadInside ? (
                                  <Spinner color="primary" size="sm" className="me-2">
                                    {t('loading.message')}
                                  </Spinner>
                                ) : (
                                  <img
                                    src={buttonChange}
                                    className="me-2"
                                    alt={t('meal.changeMealButton')}
                                  />
                                )}
                                {t('meal.changeMealButton')}
                              </Button>
                            </Col>

                            <Col xs={6}>
                              <Button
                                color="primary"
                                size="sm"
                                className="w-100 px-3"
                                onClick={handleAddMealToTracker}
                              >
                                <img
                                  src={addToTrackerIcon}
                                  alt={t('tracker.meal.addToTrackerButton')}
                                  className="me-2"
                                />
                                {t('tracker.meal.addToTrackerButton')}
                              </Button>
                            </Col>

                            <Col xs={3}>
                              <Button
                                color="link"
                                size="sm"
                                className="w-100"
                                onClick={() => {
                                  ratingRef.current.click();
                                }}
                              >
                                {meal.rating.userRating ? (
                                  <>
                                    <img
                                      src={ratingButtonFull}
                                      alt={t('meal.eatenButton')}
                                      className="me-2"
                                    />
                                    {meal.rating.userRating.rating}
                                  </>
                                ) : (
                                  <>
                                    <img
                                      src={ratingButtonEmpty}
                                      alt={t('meal.eatenButton')}
                                      className="me-2"
                                    />
                                    {t('meal.addRateButton')}
                                  </>
                                )}
                              </Button>
                            </Col>
                          </Row>
                        </div>

                        <div className="d-none d-md-block mt-xl">
                          <InstacartButton
                            meal={meal}
                            publicName={meal.name}
                            preparationImageUrl={meal.preparationImageUrl}
                          />
                          <FriscoButton meal={meal} publicName={meal.name} />
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {children()}

                  <div className="d-md-none">
                    <DayPlanNutritions kcal={meal.kcal} macro={meal.macro} />
                  </div>

                  <div className="smart-tip-container pt-0 pb-5 pb-md-4">
                    <div className="ingredients-info d-flex py-2">
                      <img src={infoIcon} style={{ width: '24px' }} alt="" className="me-2" />
                      <p className="mb-0 small">
                        {t('app.mealDetails.ingredientWeightInfo.content')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>

        <footer className="d-md-none">
          <Container key={meal.id} className="px-3">
            <Row className="gx-0">
              <Col xs={4}>
                <Button
                  color="link"
                  size="sm"
                  onClick={() => handleMealReplace()}
                  className="w-100"
                >
                  {preloadInside ? (
                    <Spinner color="primary" size="sm" className="me-2">
                      {t('loading.message')}
                    </Spinner>
                  ) : (
                    <img src={buttonChange} className="me-2" alt={t('meal.changeMealButton')} />
                  )}
                  {t('meal.changeMealButton')}
                </Button>
              </Col>
              <Col xs={8}>
                <Button
                  color="primary"
                  size="sm"
                  className="w-100 px-3"
                  onClick={handleAddMealToTracker}
                >
                  <img
                    src={addToTrackerIcon}
                    alt={t('tracker.meal.addToTrackerButton')}
                    className="me-2"
                  />
                  {t('tracker.meal.addToTrackerButton')}
                </Button>
              </Col>
            </Row>
          </Container>
        </footer>
      </div>

      <Modal isOpen={showModal} toggle={toggleModal} centered size="md">
        <ModalBody>
          <h4 className="title-medium semi-bold">{t('tracker.logMealError.popup.title')}</h4>
          <p className="text-text-variant-color multi-line mb-0">
            {t('tracker.logMealError.popup.description')}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            tag={Link}
            to="/tracker/settings"
            className="w-100"
            onClick={toggleModal}
          >
            {t('tracker.logMealError.popup.settingsButton')}
          </Button>
          <Button color="link" className="w-100" onClick={toggleModal}>
            {t('tracker.logMealError.popup.cancelButton')}
          </Button>
        </ModalFooter>
      </Modal>
    </Layout>
  );
};

const Placeholder = () => <UniversalPlaceholder titleBar="" />;

export default withLoading(withMobileAppMode(DayPlanMealDetails), Placeholder);
